import { useEffect, useRef, useState } from "react";
import { App, Input, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { getApp, updateAppName, uploadAppIcon } from "@/api/apps";
import BrandButton from "@/components/BrandButton";
import { useCurrentApp } from "@/hooks/useCurrentApp";
import { confirmDisplayName } from "@/lib/appDisplayName";
import { isAppIconSrc } from "@/lib/appIcon";
import { useAppStore } from "@/store";
import { credentialErrorText } from "./apiError";
import { Hint } from "./Hint";

const NAME_MAX = 128;
const ICON_MAX_BYTES = 2 * 1024 * 1024;
const ICON_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
]);

function isAllowedIcon(file: File): boolean {
  if (ICON_TYPES.has(file.type)) return true;
  return /\.(png|jpe?g|gif)$/i.test(file.name);
}

function AppIconPreview({ icon }: { icon: string }) {
  return (
    <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-fill text-lg">
      {isAppIconSrc(icon) ? (
        <img src={icon} alt="" className="size-full object-cover" />
      ) : (
        icon
      )}
    </div>
  );
}

export function AppIdentity() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const app = useCurrentApp();
  const upsertApp = useAppStore((s) => s.upsertApp);
  const fileRef = useRef<HTMLInputElement>(null);
  const previewUrl = useRef<string | null>(null);

  const [name, setName] = useState(app.name);
  const [syncedName, setSyncedName] = useState(app.name);
  const [icon, setIcon] = useState(app.icon);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [nameError, setNameError] = useState("");
  const [saving, setSaving] = useState(false);
  if (app.name !== syncedName) {
    setSyncedName(app.name);
    setName(app.name);
    setNameError("");
  }

  const revokePreview = () => {
    if (previewUrl.current) {
      URL.revokeObjectURL(previewUrl.current);
      previewUrl.current = null;
    }
  };

  useEffect(() => {
    setName(app.name);
    setIcon(app.icon);
    setNameError("");
    setPendingFile(null);
    revokePreview();
  }, [app.id, app.name, app.icon]);

  useEffect(() => () => revokePreview(), []);

  const trimmedName = name.trim();
  const nameInvalid = !trimmedName || trimmedName.length > NAME_MAX;
  const dirty = trimmedName !== app.name || pendingFile !== null;
  const canSave = dirty && !nameInvalid && !saving;
  const appId = app.app_id || app.id;

  const validateName = (value: string) => {
    const next = value.trim();
    if (!next) return t("credentials.nameRequired");
    if (next.length > NAME_MAX) return t("credentials.nameTooLong");
    return "";
  };

  const pickIcon = (file: File) => {
    if (!isAllowedIcon(file)) {
      message.error(t("credentials.iconType"));
      return;
    }
    if (file.size > ICON_MAX_BYTES) {
      message.error(t("credentials.iconSize"));
      return;
    }
    revokePreview();
    const url = URL.createObjectURL(file);
    previewUrl.current = url;
    setPendingFile(file);
    setIcon(url);
  };

  const resetDraft = () => {
    revokePreview();
    setPendingFile(null);
    setName(app.name);
    setIcon(app.icon);
    setNameError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const save = async () => {
    const nextError = validateName(name);
    setNameError(nextError);
    if (nextError || saving) return;
    setSaving(true);
    try {
      let nextName = app.name;
      let nextIcon = app.icon;
      if (trimmedName !== app.name) {
        nextName = await updateAppName(appId, trimmedName);
        confirmDisplayName(appId, nextName);
        upsertApp({ ...app, name: nextName });
        setName(nextName);
      }
      if (pendingFile) {
        const uploaded = await uploadAppIcon(appId, pendingFile);
        nextIcon = uploaded || (await getApp(appId)).app.icon;
        revokePreview();
        setPendingFile(null);
        upsertApp({ ...app, name: nextName, icon: nextIcon });
        setIcon(nextIcon);
      }
      message.success(t("credentials.profileSaved"));
    } catch (err) {
      message.error(credentialErrorText(t, err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex items-center gap-1.5 text-sm text-secondary">
        <span>{t("credentials.appIcon")}</span>
        <Hint title={t("credentials.appIconHint")} />
      </div>

      <div className="flex items-end gap-3">
        <AppIconPreview icon={icon} />
        <BrandButton
          variant="outline"
          disabled={saving}
          onClick={() => fileRef.current?.click()}
          style={{ borderRadius: 999 }}
        >
          {t("credentials.changeIcon")}
        </BrandButton>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/gif"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) pickIcon(file);
          }}
        />
      </div>

      <div className="flex items-center gap-4">
        <Input
          value={name}
          maxLength={NAME_MAX}
          status={nameError ? "error" : undefined}
          aria-label={t("credentials.appName")}
          style={{
            width: "360px",
          }}
          onChange={(e) => {
            setName(e.target.value);
            setNameError("");
          }}
          onBlur={() => setNameError(validateName(name))}
        />
        <BrandButton
          disabled={!canSave}
          loading={saving}
          onClick={() => {
            void save();
          }}
        >
          {t("common.save")}
        </BrandButton>
        <BrandButton
          variant="outline"
          disabled={!dirty || saving}
          onClick={resetDraft}
        >
          {t("common.cancel")}
        </BrandButton>
      </div>
      {nameError ? (
        <Typography.Text type="danger">{nameError}</Typography.Text>
      ) : null}
    </div>
  );
}
